/**
 * 靜態資源與 Cloud Storage URL 路徑轉換工具模組
 * 包含環境變數讀取、圖檔 CDN URL 拼接、WebP 相容度單例檢測與檔案下載路徑生成。
 */

const cloudStorageUrl = process.env.VUE_APP_CLOUD_STORAGE_URL;
const useCloudStorage = process.env.VUE_APP_USE_CLOUD_STORAGE === "true";
const useWebp = process.env.VUE_APP_USE_WEBP === "true";

/**
 * 檢查當前環境變數是否開啟 WebP 支援功能
 * @returns {boolean} 是否啟用 VUE_APP_USE_WEBP
 */
export function isWebpEnabled() {
  return process.env.VUE_APP_USE_WEBP === "true";
}

/**
 * 取得靜態圖片之完整載入 URL (自動判斷開發環境與正式 CDN 環境)
 * @param {string|object} src 傳入之圖片檔名、相對路徑或 require 物件
 * @returns {string} 轉換後可直接供 <img> 或 CSS 讀取之 URL
 */
export function getStaticImageUrl(src) {
  // 1. 若傳入為 require() 或 import 導出的物件 (包含 .default 屬性)
  if (typeof src === "object" && src.default) {
    return src.default;
  }

  // 2. 若傳入已是完整遠端 URL (http://, https://, data: 等前綴)
  if (
    typeof src === "string" &&
    (src.startsWith("http://") ||
      src.startsWith("https://") ||
      src.startsWith("data:"))
  ) {
    return src;
  }

  const cloudStorageUrl = process.env.VUE_APP_CLOUD_STORAGE_URL;
  const useCloudStorage = process.env.VUE_APP_USE_CLOUD_STORAGE === "true";

  // 清理字串相對路徑（移除前綴如 "@/assets/img/", "/static/img/", "/img/" 或開頭斜線，保留子目錄如 "news/xx.png"）
  let cleanPath = "";
  if (typeof src === "string") {
    cleanPath = src
      .replace(/^@\/assets\/img\//, "")
      .replace(/^\/static\/img\//, "")
      .replace(/^static\/img\//, "")
      .replace(/^\/img\//, "")
      .replace(/^img\//, "")
      .replace(/^\//, "");
  }

  // 3. 正式 Release 環境 (使用 Cloud Storage CDN 伺服器)
  if (useCloudStorage && cloudStorageUrl) {
    const baseUrl = cloudStorageUrl.endsWith("/")
      ? cloudStorageUrl
      : cloudStorageUrl + "/";
    return `${baseUrl}images/${cleanPath}`;
  }

  // 4. 開發與本機預覽環境 (使用 Webpack require 動態載入)
  if (typeof src === "string") {
    // A. 如果已經被 Webpack 處理成發布路徑 (如 /OfficialWebsite/static/img/... 或 /static/...)
    if (
      src.includes("/static/") ||
      src.includes("/img/") ||
      src.startsWith("/")
    ) {
      return src;
    }

    // B. 嘗試透過 require 動態載入本地 src/assets/img/ 下的圖檔
    try {
      return require(`@/assets/img/${cleanPath}`);
    } catch (e) {
      return src;
    }
  }

  return src;
}

/** 瀏覽器 WebP 檢測單例快取變數 */
let isWebpSupported = null;

/**
 * 檢測當前瀏覽器是否支援 WebP 圖檔格式 (單例快取模式，僅執行一次 Canvas 特性檢測)
 * @returns {boolean} 瀏覽器是否支援 WebP
 */
export function checkWebpSupport() {
  // 若已檢測過，直接回傳全域快取結果
  if (isWebpSupported !== null) return isWebpSupported;

  // 若全域環境變數未開啟，或非瀏覽器環境 (SSR)，直接回傳 false
  if (
    !useWebp ||
    typeof window === "undefined" ||
    typeof document === "undefined"
  ) {
    isWebpSupported = false;
    return false;
  }

  // 使用 HTML5 Canvas 嘗試導出 WebP Data URL 進行實際相容度測試
  try {
    const elem = document.createElement("canvas");
    if (elem && elem.getContext && elem.getContext("2d")) {
      isWebpSupported =
        elem.toDataURL("image/webp").indexOf("data:image/webp") === 0;
    } else {
      isWebpSupported = false;
    }
  } catch (e) {
    isWebpSupported = false;
  }
  return isWebpSupported;
}

/**
 * 取得檔案下載之完整 URL (自動判斷開發環境與正式 Cloud Storage 環境)
 * @param {string} fileName 要下載之檔案名稱
 * @returns {string} 完整下載連結 URL
 */
export function getStaticDownLoadUrl(fileName) {
  if (useCloudStorage && cloudStorageUrl) {
    const baseUrl = cloudStorageUrl.endsWith("/")
      ? cloudStorageUrl
      : cloudStorageUrl + "/";
    return `${baseUrl}download/${fileName}`;
  } else {
    return `/${process.env.VUE_APP_AP_NAME}/download/${fileName}`;
  }
}
